import { ACBSP_GRADUATES_SERIES, GRADUATES_SERIES } from '@/data/experience';
import { AnimatedCounter, ScrollReveal, SplitReveal } from '@/components/motion';

/** Accredited-programme graduate totals, computed (never hardcoded): MBA 166, DBA 36. */
const ACBSP_MBA_TOTAL = ACBSP_GRADUATES_SERIES.reduce((sum, row) => sum + row.mba, 0);
const ACBSP_DBA_TOTAL = ACBSP_GRADUATES_SERIES.reduce((sum, row) => sum + row.dba, 0);

/** Institution-wide five-year graduate total and the endpoints of the growth strip. */
const GRADUATES_TOTAL = GRADUATES_SERIES.reduce((sum, row) => sum + row.count, 0);
const GRADUATES_MAX = Math.max(...GRADUATES_SERIES.map((g) => g.count));
const GRADUATES_FIRST = GRADUATES_SERIES[0].count;
const GRADUATES_LAST = GRADUATES_SERIES[GRADUATES_SERIES.length - 1].count;

/**
 * GraduatesDetail (/experience/student-achievement-rates ★ ACBSP-mandated) —
 * "Graduates, 2021–2025". Two parts, both fully accessible as real numbers:
 *   1) a semantic <table> of ACBSP accredited-programme graduates (Year / MBA /
 *      DBA) with a computed TOTAL row (reduce() → MBA 166, DBA 36); and
 *   2) an institution-wide (all programmes) growth strip mirroring the
 *      achievement-rates.tsx graduates idiom — div bars scaled to the cohort max,
 *      each value printed via AnimatedCounter + aria-label, a 319 → 1,275 summary
 *      and the computed five-year total.
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger for the table and the
 * bar strip, AnimatedCounter for every figure. All primitives are reduced-motion
 * safe and never strand content hidden; bar heights are inline (final height
 * always rendered). Every number is derived from ACBSP_GRADUATES_SERIES /
 * GRADUATES_SERIES in @/data/experience — nothing invented; totals via reduce().
 */
export function GraduatesDetail() {
  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Graduates
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Graduates, 2021–2025.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              The full graduate record — ACBSP-accredited MBA and DBA cohorts year by year, and
              institution-wide graduates across all programmes. All figures are institution-reported
              for 2021–2025.
            </p>
          </ScrollReveal>
        </header>

        {/* ── 1 · ACBSP accredited-programme graduates (table) ────────── */}
        <div className="mt-14 border-t border-border pt-12">
          <h3 className="font-display text-xl leading-tight font-semibold text-foreground">
            ACBSP accredited-programme graduates
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            MBA and DBA graduates from the ACBSP-accredited programmes, 2021–2025.
          </p>

          <ScrollReveal from="up" delay={0.1} className="mt-8 overflow-x-auto">
            <table className="w-full min-w-[28rem] border-collapse text-left">
              <caption className="sr-only">
                ACBSP-accredited MBA and DBA graduates by year, 2021 to 2025, with five-year totals.
                Institution-reported.
              </caption>
              <thead>
                <tr className="border-b border-border">
                  <th
                    scope="col"
                    className="font-label pb-3 text-xs tracking-[0.18em] text-muted-foreground uppercase"
                  >
                    Year
                  </th>
                  <th
                    scope="col"
                    className="font-label pb-3 text-right text-xs tracking-[0.18em] text-muted-foreground uppercase"
                  >
                    MBA
                  </th>
                  <th
                    scope="col"
                    className="font-label pb-3 text-right text-xs tracking-[0.18em] text-muted-foreground uppercase"
                  >
                    DBA
                  </th>
                </tr>
              </thead>
              <tbody>
                {ACBSP_GRADUATES_SERIES.map((row) => (
                  <tr key={row.year} className="border-b border-border/60">
                    <th
                      scope="row"
                      className="py-3 text-sm font-medium text-foreground tabular-nums"
                    >
                      {row.year}
                    </th>
                    <td className="py-3 text-right text-sm text-foreground tabular-nums">
                      {row.mba}
                    </td>
                    <td className="py-3 text-right text-sm text-foreground tabular-nums">
                      {row.dba}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-brand-navy/20">
                  <th
                    scope="row"
                    className="font-label pt-3 text-xs tracking-[0.18em] text-brand-navy uppercase"
                  >
                    Total
                  </th>
                  <td className="pt-3 text-right font-display text-base font-semibold text-brand-navy tabular-nums">
                    {ACBSP_MBA_TOTAL}
                  </td>
                  <td className="pt-3 text-right font-display text-base font-semibold text-brand-navy tabular-nums">
                    {ACBSP_DBA_TOTAL}
                  </td>
                </tr>
              </tfoot>
            </table>
          </ScrollReveal>
        </div>

        {/* ── 2 · Institution-wide graduates growth strip ─────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="font-display text-xl leading-tight font-semibold text-foreground">
                Institution-wide graduates
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Annual graduate cohort across all programmes, 2021–2025.
              </p>
            </div>
            <p className="font-display text-sm text-muted-foreground">
              <span className="font-semibold text-brand-navy tabular-nums">
                {GRADUATES_FIRST.toLocaleString('en-GB')}
              </span>
              <span className="mx-2" aria-hidden>
                →
              </span>
              <span className="font-semibold text-brand-navy tabular-nums">
                {GRADUATES_LAST.toLocaleString('en-GB')}
              </span>
            </p>
          </div>

          <div className="mt-8 rounded-2xl border border-border bg-background p-7 shadow-sm">
            <ScrollReveal stagger={0.08} className="flex h-44 items-end justify-between gap-3">
              {GRADUATES_SERIES.map((row) => (
                <div
                  key={row.year}
                  className="flex h-full flex-1 flex-col items-center justify-end gap-2"
                >
                  <AnimatedCounter
                    value={row.count}
                    grouping
                    className="font-display text-xs font-semibold text-foreground tabular-nums"
                  />
                  <div
                    className="w-full rounded-t-md bg-brand-gold"
                    style={{ height: `${(row.count / GRADUATES_MAX) * 100}%` }}
                    role="img"
                    aria-label={`Institution-wide graduates, ${row.year}: ${row.count.toLocaleString('en-GB')}`}
                  />
                  <span className="text-xs text-muted-foreground tabular-nums">{row.year}</span>
                </div>
              ))}
            </ScrollReveal>
          </div>

          {/* ── Five-year total ───────────────────────────────────────── */}
          <ScrollReveal from="up" delay={0.1}>
            <div className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-2xl border border-border bg-background p-6 shadow-sm">
              <AnimatedCounter
                value={GRADUATES_TOTAL}
                grouping
                className="font-display text-3xl leading-none font-semibold text-brand-navy tabular-nums md:text-4xl"
              />
              <p className="text-sm text-muted-foreground">
                graduates in total, all programmes · 2021–2025
              </p>
            </div>
          </ScrollReveal>
        </div>

        {/* ── Sourcing caption ────────────────────────────────────────── */}
        <p className="mt-10 border-t border-border pt-8 text-xs text-muted-foreground">
          Figures are institution-reported for 2021–2025.
        </p>
      </div>
    </section>
  );
}
